import {
  faSearchMinus,
  faSearchPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Loader } from 'src/components/Loader';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import {
  PlanBlock,
  PlanWorkout,
  fetchPlanBlock,
  fetchPlanWorkout,
  fetchWorkoutPlan,
} from 'src/models/workout-plan/workout-plan';
import api from 'src/utils/ajax';
import { simpleFormat } from 'src/utils/simpleFormat';
import toast from 'src/utils/toast';

import './TVDisplayPage.css';
import {
  blockTextSummary,
  ensureSortedBySortOrder,
  isTruthyParam,
  summaryString,
} from './utils';

export default function TVDisplayPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'tvDisplay',
  });
  const { session, user, disableDarkmode, enableDarkmode } = useOptionalSession();

  useLayoutEffect(() => {
    disableDarkmode();
    return () => {
      enableDarkmode();
    };
  }, [disableDarkmode, enableDarkmode]);
  const [searchParams] = useSearchParams();

  const tvGridRef = useRef<HTMLDivElement | null>(null);
  const zoomTextRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  const blockId = searchParams.get('blockId');
  const workoutId = searchParams.get('workoutId');
  const textOnlyParam = isTruthyParam(searchParams.get('textonly'));

  useEffect(() => {
    const toastId = toast(
      <Typography variant="h5">{t('zoomTip')}</Typography>,
      {
        duration: 15000,
        position: 'top-center',
      }
    );
    return () => {
      toast.remove(toastId);
    };
  }, [t]);

  const handleZoom = (zoomIn: boolean) => {
    const container = zoomTextRef.current;
    if (!container) return;

    const increment = zoomIn ? 2 : -2;
    const elements = container.querySelectorAll<HTMLElement>('*');
    const first = elements.item(0);
    if (!first) return;

    const computed = window.getComputedStyle(first);
    const fontSizePx = Number.parseFloat(computed.fontSize || '');
    if (!Number.isFinite(fontSizePx)) return;

    const nextSize = Math.max(1, fontSizePx + increment);
    elements.forEach((el) => {
      el.style.fontSize = `${nextSize}px`;
    });
  };

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevMargin = document.body.style.margin;
    const prevHeight = document.body.style.height;
    const prevWidth = document.body.style.width;
    const prevBg = document.body.style.backgroundColor;

    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.height = '100vh';
    document.body.style.width = '100vw';
    document.body.style.backgroundColor = 'white';

    const style = document.createElement('style');
    style.setAttribute('data-tv-display', 'true');
    style.textContent = `
      .site-header, .site-footer { display: none !important; }
      #intercom-container { display: none !important; }
      body::-webkit-scrollbar { width: 0 !important; height: 0 !important; }
      .flash-wrapper  { position: fixed; top: 0; right: 0; left: 0; width: 100% !important; max-width: 100% !important; }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.margin = prevMargin;
      document.body.style.height = prevHeight;
      document.body.style.width = prevWidth;
      document.body.style.backgroundColor = prevBg;
      style.remove();
    };
  }, []);

  const planWorkoutQuery = useQuery({
    queryKey: ['tvDisplay', 'workout', workoutId],
    queryFn: async (): Promise<PlanWorkout> => {
      return await fetchPlanWorkout(workoutId!);
    },
    enabled: !!workoutId,
  });

  const planBlockQuery = useQuery({
    queryKey: ['tvDisplay', 'block', blockId],
    queryFn: async (): Promise<PlanBlock> => {
      return await fetchPlanBlock(blockId!);
    },
    enabled: !!blockId,
  });
  const workoutIdFromBlock =
    planBlockQuery.data?.workoutId ?? planBlockQuery.data?.workoutId;
  const blockWorkoutQuery = useQuery({
    queryKey: ['tvDisplay', 'blockWorkout', workoutIdFromBlock],
    queryFn: async (): Promise<PlanWorkout> => {
      return await fetchPlanWorkout(workoutIdFromBlock!);
    },
    enabled: !!blockId && !!workoutIdFromBlock,
  });

  const workoutPlanQuery = useQuery({
    queryKey: [
      'tvDisplay',
      'workoutPlan',
      planWorkoutQuery.data?.workoutPlanId,
      blockWorkoutQuery.data?.workoutPlanId,
    ],
    queryFn: async () => {
      const planId =
        planWorkoutQuery.data?.workoutPlanId ??
        blockWorkoutQuery.data?.workoutPlanId;

      if (!planId) return null;
      return fetchWorkoutPlan({ id: planId });
    },
    enabled: !!(
      planWorkoutQuery.data?.workoutPlanId ||
      blockWorkoutQuery.data?.workoutPlanId
    ),
  });

  const usesMetric = user?.usesMetric ?? false;
  const plan = workoutPlanQuery.data;
  const useMetric =
    plan && !plan.usesNewCreator && plan.useMetric !== undefined
      ? plan.useMetric
      : usesMetric;

  const logoUrl = session?.logoUrl;

  const isLoading =
    planWorkoutQuery.isLoading ||
    planBlockQuery.isLoading ||
    blockWorkoutQuery.isLoading ||
    workoutPlanQuery.isLoading;

  useEffect(() => {
    if (!loaded) return;
    const gridEl = tvGridRef.current;
    if (!gridEl) return;

    // Only apply in the with-images branch (mirrors the hbs logic).
    if (!gridEl.classList.contains('with-images')) return;

    const applyHeights = () => {
      const isLarge =
        gridEl.classList.contains('tv-workout-grid-5-8') &&
        gridEl.classList.contains('with-images');
      const isSmall =
        gridEl.classList.contains('tv-workout-grid-1-4') &&
        gridEl.classList.contains('with-images');

      if (!isLarge && !isSmall) return;

      const images = gridEl.querySelectorAll('img');
      const first = images.item(0);
      if (!first) return;

      const width = first.getBoundingClientRect().width;
      if (!width) return;

      const heightPx = isLarge ? Math.max(0, width - 60) : Math.max(0, width);

      for (let i = 0; i < images.length; i += 1) {
        images.item(i).style.height = `${heightPx}px`;
      }
    };

    applyHeights();
  }, [blockId, planBlockQuery.data, textOnlyParam, loaded]);

  const error =
    planWorkoutQuery.error ||
    planBlockQuery.error ||
    blockWorkoutQuery.error ||
    workoutPlanQuery.error;

  const pageTitle = '';

  if (!blockId && !workoutId) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography variant="h5">{t('missingRequiredQueryParam')}</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Meta title={pageTitle} brand={session?.brand} />
        <Loader size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Meta title={pageTitle} brand={session?.brand} />
        <Typography variant="h6" color="error">
          {api.formatError(error, t('failedToLoadTvDisplay'))}
        </Typography>
      </Box>
    );
  }

  if (workoutId && planWorkoutQuery.data) {
    const workout = planWorkoutQuery.data;
    const entryType = (workout.entryType ?? '').toString();
    const workoutNotes = workout.notes ?? workout.workoutDescription ?? '';
    const sortedBlocks = ensureSortedBySortOrder(
      (workout.blocks ?? []).map((b) => ({
        ...b,
        sortOrder: b.sortOrder,
      }))
    );

    const isTextOnlyWorkout = entryType === 'text';
    const isTextWithExercises = entryType === 'text_with_exercises';

    const blockGroups = sortedBlocks.reduce<PlanBlock[][]>(
      (groups, block, index) => {
        const groupIndex = Math.floor(index / 2);
        if (!groups[groupIndex]) groups[groupIndex] = [];
        groups[groupIndex].push(block);
        return groups;
      },
      []
    );

    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          backgroundColor: '#fff',
          color: 'text.primary',
          overflow: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
          },
        }}
        className="full-workout tv-workout-wrap"
      >
        <Meta title={pageTitle} brand={session?.brand} />
        <Stack spacing={2} sx={{ height: '100%', width: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            className="block-header"
            sx={{
              px: 2,
              py: 1,
            }}
          >
            <Box
              className="block-header-wrapper"
              sx={{ display: 'flex', alignItems: 'flex-start' }}
            >
              {logoUrl ? (
                <Box component="img" src={logoUrl} sx={{ height: '2.4vmin' }} />
              ) : null}
              <Typography
                component="i"
                variant="h6"
                sx={{ fontStyle: 'italic' }}
              >
                {workout.name || ''}
              </Typography>
              <Box sx={{ ml: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title={t('zoomOut')}>
                    <Link
                      component="button"
                      onClick={() => handleZoom(false)}
                      style={{ padding: '0px 5px', fontSize: 18 }}
                    >
                      <Icon icon={faSearchMinus} />
                    </Link>
                  </Tooltip>
                  <Tooltip title={t('zoomIn')}>
                    <Link
                      component="button"
                      onClick={() => handleZoom(true)}
                      style={{ fontSize: 18 }}
                    >
                      <Icon icon={faSearchPlus} />
                    </Link>
                  </Tooltip>
                </Stack>
              </Box>
            </Box>
          </Stack>

          {isTextOnlyWorkout ? (
            <Box className="zoom-text" ref={zoomTextRef} sx={{ px: 2 }}>
              <HtmlTypography sx={{ whiteSpace: 'pre-wrap' }}>
                {simpleFormat(workoutNotes, true)}
              </HtmlTypography>
            </Box>
          ) : (
            <Box className="zoom-text" ref={zoomTextRef} sx={{ px: 2 }}>
              {workoutNotes ? (
                <HtmlTypography
                  sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap', mb: 2 }}
                >
                  {simpleFormat(workoutNotes, true)}
                </HtmlTypography>
              ) : null}

              <Stack spacing={3} sx={{ width: '100%' }}>
                {blockGroups.map((pair, rowIndex) => (
                  <Stack
                    key={rowIndex}
                    direction="row"
                    spacing={2}
                    sx={{
                      width: '100%',
                      alignItems: 'stretch',
                    }}
                  >
                    {pair.map((block, colIndex) => {
                      const sortedExercises = ensureSortedBySortOrder(
                        (block.exercises ?? []).map((e) => ({
                          ...e,
                          sortOrder: e.sortOrder,
                        }))
                      );

                      const blockIndex =
                        (block as any).blockIndex ?? rowIndex * 2 + colIndex;
                      const blockName =
                        block.name ||
                        t('blockLetter', {
                          letter: String.fromCharCode(65 + blockIndex),
                        });
                      const blockSummaryWithoutName = blockTextSummary(block)
                        .replace(block.name || '', '')
                        .replace(/^\s*-\s*/, '');

                      return (
                        <Box
                          key={String(block.id)}
                          sx={{
                            flex: 0.5,
                            position: 'relative',
                            pr: colIndex === 0 ? 0 : undefined,
                          }}
                        >
                          <Stack spacing={1} sx={{ pb: 3 }}>
                            <Typography variant="h6" align="center">
                              {String(blockIndex + 1)}
                            </Typography>

                            {block.blockType?.toLowerCase() !==
                            'placeholder' ? (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {blockName}
                                  {blockSummaryWithoutName
                                    ? ` - ${blockSummaryWithoutName}`
                                    : ''}
                                </Typography>
                                {block.notes ? (
                                  <HtmlTypography
                                    sx={{
                                      fontStyle: 'italic',
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {simpleFormat(block.notes, true)}
                                  </HtmlTypography>
                                ) : null}
                              </>
                            ) : null}

                            {sortedExercises.map((ex) => (
                              <Box key={String(ex.id)}>
                                <Typography variant="h6" color="primary">
                                  {ex.exercise?.name || ''}
                                </Typography>
                                {!isTextWithExercises ? (
                                  <Typography
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                    color="secondary"
                                  >
                                    {summaryString(ex, false, useMetric)}
                                  </Typography>
                                ) : null}
                                {ex.notes ? (
                                  <HtmlTypography
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                    color="secondary"
                                  >
                                    {simpleFormat(ex.notes, true)}
                                  </HtmlTypography>
                                ) : null}
                              </Box>
                            ))}
                          </Stack>
                          {sortedExercises.length > 0 ? (
                            <Box
                              component="hr"
                              sx={{
                                position: 'absolute',
                                width: '100%',
                                bottom: 0,
                                m: 0,
                                borderColor: 'rgba(0,0,0,0.25)',
                              }}
                            />
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    );
  }

  if (blockId && blockWorkoutQuery.data) {
    const block = blockWorkoutQuery.data?.blocks.find((o) => o.id == blockId);
    const exercisesCount = (block?.exercises ?? []).length;
    const gridClass = exercisesCount > 4 ? 'large' : 'small';
    const withImages = !textOnlyParam;
    const sortedExercises = ensureSortedBySortOrder(
      (block?.exercises ?? []).map((e) => ({
        ...e,
        sortOrder: e.sortOrder,
      }))
    );

    const gridColumns = exercisesCount <= 4 ? 2 : withImages ? 4 : 3;

    if (!block) {
      return null;
    }

    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          backgroundColor: '#fff',
          overflow: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
          },
        }}
        className="tv-workout-wrap"
      >
        <Meta title={pageTitle} brand={session?.brand} />
        <Stack spacing={1} sx={{ height: '100%', width: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            className={`block-header${exercisesCount > 4 ? ' medium-grid' : ''}`}
            sx={{
              px: gridClass === 'small' ? 4 : 2,
              py: 1,
            }}
          >
            {logoUrl ? (
              <Box component="img" src={logoUrl} sx={{ height: '2.4vmin' }} />
            ) : null}
            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
              {blockTextSummary(block)}
            </Typography>
          </Stack>

          <Box
            ref={(node) => {
              if (node instanceof HTMLDivElement) {
                setLoaded(true);
                tvGridRef.current = node;
              }
            }}
            className={
              withImages
                ? `tv-workout-grid with-images ${exercisesCount > 4 ? 'tv-workout-grid-5-8' : 'tv-workout-grid-1-4'}`
                : `tv-workout-grid text-only ${exercisesCount > 4 ? 'tv-workout-grid-5-8' : 'tv-workout-grid-1-4'}`
            }
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
              gap: 2,
              px: 2,
              pb: 2,
              height: 'calc(100vh - 60px)',
              width: '100vw',
              overflow: 'hidden',
            }}
          >
            {sortedExercises.map((exercise, index) => {
              const imageSrc =
                exercise.exercise?.squareThumbnail ||
                exercise.exercise?.thumbnail;

              return (
                <Box
                  key={String(exercise.id)}
                  className="tv-workout-grid-item"
                  sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    boxShadow: !withImages
                      ? '0 0 0 1px rgba(14, 41, 57, 0.12), 0 2px 5px rgba(14, 41, 57, 0.12)'
                      : undefined,
                    borderRadius: 1,
                    display: 'flex',
                    padding: '1px',
                  }}
                >
                  <Stack
                    direction={withImages ? 'column' : 'row'}
                    className="item-content"
                    sx={{
                      width: '100%',
                      p: withImages ? 0 : 2,
                      alignItems: withImages ? 'stretch' : 'flex-start',
                      position: 'relative',
                    }}
                  >
                    {withImages && imageSrc ? (
                      <Box component="img" src={imageSrc} />
                    ) : null}

                    <Stack spacing={1} className="body-wrapper">
                      <Typography className="exercise-name">
                        <Typography className="index-number" component="span">
                          {String(index + 1)}
                        </Typography>
                        {exercise.exercise?.name || ''}
                        {exercise.eachSide ? (
                          <Typography
                            component="span"
                            className="alternate-wrapper"
                            sx={{ fontSize: '1.2rem' }}
                          >
                            {t('performEachSideSuffix')}
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography
                        sx={{
                          fontStyle: 'italic',
                          whiteSpace: 'pre-wrap',
                          fontSize: '1.2rem',
                          fontWeight: 200,
                        }}
                      >
                        {summaryString(exercise, false, useMetric, true)}
                      </Typography>

                      {(exercise.alternateExercises ?? []).length > 0 ? (
                        <Box className="alternate-wrapper">
                          {(exercise.alternateExercises ?? []).map(
                            (alt: any, altIndex: number) => {
                              // the sets are not displayed for alternates in old system
                              const altSummary = summaryString(
                                { ...alt, sets: null },
                                true,
                                useMetric,
                                true
                              );

                              return (
                                <Typography
                                  key={`${String(exercise.id)}-alt-${altIndex}`}
                                  sx={{ whiteSpace: 'pre-wrap' }}
                                >
                                  <b>
                                    {t('alternateLabel', { name: alt.name })}
                                  </b>
                                  : <i>{altSummary}</i>
                                </Typography>
                              );
                            }
                          )}
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Stack>
      </Box>
    );
  }

  return null;
}
